/**
 * Spec 106 — PersonaEvaluationsPage.
 *
 * Lists the selected brand's response persona-fit findings (observational lens)
 * and provides a "Run response persona-fit" trigger with persona + cluster
 * pickers. Each finding renders as a PersonaFindingCard, decomposed into
 * priority / [wellbeing] / trust / anti-signal with the observational label,
 * persona-confidence badge, and sample-size + CI.
 *
 * The cluster picker lists fan-out clusters by slug and resolves the chosen
 * slug to its UUID (the detail route returns `id`) before enqueuing — staying
 * read-only against the 086 fan-out routes.
 *
 * data-testids: run-persona-eval, persona-picker, cluster-picker (+ the card's
 * own testids).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';
import { listPersonas } from '../api/personas.js';
import type { Persona } from '../api/personas.js';
import {
  listFanoutClusters,
  getFanoutCluster,
  type FanoutClusterRow,
} from '../api/fanout.js';
import {
  listPersonaFindings,
  getPersonaFinding,
  runPersonaEvaluation,
  type PersonaResponseFinding,
} from '../api/personaEvaluation.js';
import { PersonaFindingCard } from '../components/PersonaFindingCard.js';
import { EmptyState } from '../components/EmptyState.js';
import { Select } from '../components/Select.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { RowList, Row } from '../components/ui/Row.js';

export function PersonaEvaluationsPage() {
  const { selectedBrand } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const brandId = selectedBrand?.id ?? null;

  const canRun = useMemo(
    () => hasRole('rule_admin') || hasRole('orbit:admin'),
    [hasRole],
  );

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [clusters, setClusters] = useState<FanoutClusterRow[]>([]);
  const [findings, setFindings] = useState<PersonaResponseFinding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState('');
  const [selectedClusterSlug, setSelectedClusterSlug] = useState('');
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const [p, c, f] = await Promise.all([
        listPersonas(brandId).catch(() => []),
        listFanoutClusters(brandId).catch(() => []),
        listPersonaFindings(brandId).catch(() => []),
      ]);
      setPersonas(p);
      setClusters(c);
      // Hydrate dimension results for completed findings (for the decomposition).
      const hydrated = await Promise.all(
        f.map((row) =>
          getPersonaFinding(brandId, row.id).catch(() => row),
        ),
      );
      setFindings(hydrated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRun = useCallback(async () => {
    if (!brandId || !selectedPersona || !selectedClusterSlug) return;
    setRunning(true);
    try {
      const detail = await getFanoutCluster(brandId, selectedClusterSlug);
      await runPersonaEvaluation(brandId, {
        persona_id: selectedPersona,
        query_cluster_id: detail.id,
      });
      addToast('AI-answer persona-fit evaluation enqueued', 'success');
      await refresh();
    } catch (e) {
      addToast(
        e instanceof Error ? e.message : 'Failed to enqueue evaluation',
        'error',
      );
    } finally {
      setRunning(false);
    }
  }, [brandId, selectedPersona, selectedClusterSlug, addToast, refresh]);

  if (!brandId) {
    return <div className="persona-evaluations-page">Select a brand to view findings.</div>;
  }

  return (
    <div className="persona-evaluations-page hub-page--table" data-testid="persona-evaluations-page">
      <Breadcrumbs
        items={[
          { label: 'Personas', to: '/personas' },
          { label: 'AI-answer persona-fit' },
        ]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-message" className="text-violet-600" />
          AI-answer persona-fit
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          The <strong>observational</strong> lens — what the AI engine actually did for a
          persona on a query cluster, measured from the answers GEO Observatory captured. Never
          blended with the GEO score; carries only the persona-definition caveat.
        </p>
      </header>

      {canRun && !loading && clusters.length === 0 && (
        <div
          data-testid="persona-eval-no-clusters-hint"
          className="run-persona-eval-bar rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex flex-wrap items-center gap-2"
        >
          <Icon name="bx-sitemap" className="text-amber-600 shrink-0" />
          <span>No query clusters yet — run fan-out synthesis first.</span>
          <Link
            to="/fanout"
            className="inline-flex items-center font-medium text-violet-700 underline underline-offset-2 hover:text-violet-800"
          >
            Go to fan-out
          </Link>
        </div>
      )}

      {canRun && clusters.length > 0 && (
        <div
          className="run-persona-eval-bar hub-plate flex flex-wrap items-center gap-3"
        >
          <Select
            data-testid="persona-picker"
            className="min-w-56"
            placeholder="Pick a persona…"
            value={selectedPersona}
            onChange={(v) => setSelectedPersona(v)}
            options={personas.map((p) => ({
              value: p.persona_id,
              label: `${p.name} (${p.persona_type})`,
            }))}
          />
          <Select
            data-testid="cluster-picker"
            className="min-w-56"
            placeholder="Pick a query cluster…"
            value={selectedClusterSlug}
            onChange={(v) => setSelectedClusterSlug(v)}
            options={clusters.map((c) => ({
              value: c.slug,
              label: c.semantic_label || c.slug,
            }))}
          />
          <Button
            variant="primary"
            data-testid="run-persona-eval"
            disabled={running || !selectedPersona || !selectedClusterSlug}
            onClick={() => void onRun()}
          >
            {running ? 'Running…' : 'Run response persona-fit'}
          </Button>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}
      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {findings.length === 0 && !loading && (
        <div className="mt-3">
          <EmptyState
            icon="bx-message"
            variant="no-results"
            title="No response persona-fit findings yet"
            description={
              clusters.length === 0
                ? 'There are no query clusters to evaluate yet — run fan-out synthesis first.'
                : 'Pick a persona and a query cluster above, then run an evaluation.'
            }
          />
        </div>
      )}
      {findings.length > 0 && (
        <RowList className="mt-3">
          {findings.map((f) => {
            // Spec 235 §3.3 — a disclosure row: PersonaFindingCard is an
            // accordion (aria-expanded, zero links), not a navigable item, so
            // Row is given no `to`. It renders the hairline/padding container
            // and nothing else; the card's own toggles are untouched below.
            // Its own header never names WHICH persona a finding is about
            // (only its version number), so the row's title looks the
            // persona up by id from the page's own already-fetched list —
            // no new request, no change to PersonaFindingCard itself.
            const persona = personas.find((p) => p.persona_id === f.persona_id);
            return (
              <Row key={f.id} title={persona?.name ?? `Persona v${f.persona_version}`}>
                <PersonaFindingCard finding={f} />
              </Row>
            );
          })}
        </RowList>
      )}
    </div>
  );
}
